/**
 * Attempts to extract a session_id from a URL string.
 * If the input is a URL containing a session_id query parameter, returns that parameter.
 * Otherwise, returns the original input unchanged.
 *
 * @param input - A string that might be a URL with a session_id parameter or just a session_id
 * @returns The extracted session_id or the original input
 *
 * @example
 * // Returns: "9558a788-04a7-4323-855c-723703ace454"
 * extractSessionIdFromUrl("https://console.moveo.ai/analytics?session_id=9558a788-04a7-4323-855c-723703ace454")
 *
 * @example
 * // Returns: "9558a788-04a7-4323-855c-723703ace454" (original input)
 * extractSessionIdFromUrl("9558a788-04a7-4323-855c-723703ace454")
 */
export const extractSessionIdFromUrl = (input: string): string => {
  if (!input) {
    return input;
  }

  try {
    // Try to parse as URL - handles both full URLs and relative paths
    const url = new URL(input, 'https://dummy.com');
    const sessionId = url.searchParams.get('session_id');

    // If we found a session_id parameter, return it
    if (sessionId) {
      return sessionId.trim();
    }
  } catch {
    // Not a valid URL, could be a plain session_id string
    // Fall through to return original input
  }

  // Return original input if no session_id was found in URL
  return input;
};

export const urlWithoutSessionId = (): string => {
  const url = new URL(window.location.href);
  url.searchParams.delete('session_id');
  return url.pathname + url.search;
};
